class Restaumatic {
    static toPlatformEvent(event) {
        return {
            name: event.tag,
            value: "contents" in event ? event.contents : {},
        };
    }
}
class AnalyticsV3 {
    static toPlatformEvent(currency, event) {
        switch (event.tag) {
            case "AddToCart":
                return { name: "AddToCart", value: {} };
            case "LocateMe":
                return { name: "LocateMe", value: {} };
            case "RemoveFromCart":
                return { name: "RemoveFromCart", value: {} };
            case "BeginCheckout":
                return { name: "BeginCheckout", value: {} };
            case "Purchase": {
                return {
                    name: "Purchase",
                    value: {
                        transaction: {
                            id: event.contents.transactionId,
                            affiliation: event.contents.affiliation,
                            revenue: event.contents.total,
                            shipping: event.contents.shipping,
                            tax: 0.0,
                            currency: currency,
                        },
                        items: event.contents.items.map((item) => {
                            return {
                                id: event.contents.transactionId,
                                name: item.name,
                                price: item.price,
                                quantity: item.quantity,
                                category: item.category,
                                currency: currency,
                            };
                        }),
                    },
                };
            }
            case "SetCheckoutOption":
                return { name: "SetCheckoutOption", value: {} };
            case "SuggestionResult":
            case "ExperienceImpression":
                return undefined;
            default:
                throw new Error("Unknown event");
        }
    }
}
class AnalyticsV4 {
    static itemValue(item) {
        return item.price * item.quantity;
    }
    static convertItem(item) {
        var _a;
        return {
            item_name: item.name,
            quantity: item.quantity,
            price: item.price,
            item_category: item.category,
            item_list_id: (_a = item.list) !== null && _a !== void 0 ? _a : undefined,
        };
    }
    static removeFromCart(currency, item) {
        return {
            currency: currency,
            value: this.itemValue(item),
            items: [this.convertItem(item)],
        };
    }
    static purchase(currency, item) {
        return {
            items: item.items.map((item) => this.convertItem(item)),
            value: item.total,
            currency: currency,
            affiliation: item.affiliation,
            shipping: item.shipping,
            transaction_id: item.transactionId,
            coupon: item.coupon,
        };
    }
    static toPlatformEvent(currency, event) {
        switch (event.tag) {
            case "AddToCart":
                return {
                    name: "AddToCart",
                    value: {
                        currency: currency,
                        value: this.itemValue(event.contents),
                        items: [this.convertItem(event.contents)],
                    },
                };
            case "LocateMe":
                return { name: "LocateMe", value: {} };
            case "RemoveFromCart":
                return {
                    name: "RemoveFromCart",
                    value: this.removeFromCart(currency, event.contents),
                };
            case "BeginCheckout":
                return { name: "BeginCheckout", value: event.contents };
            case "Purchase":
                return {
                    name: "Purchase",
                    value: this.purchase(currency, event.contents),
                };
            case "SetCheckoutOption":
                return { name: "SetCheckoutOption", value: event.contents };
            case "SuggestionResult":
                return undefined;
            case "ExperienceImpression":
                return {
                    name: "experience_impression",
                    value: {
                        exp_variant_string: `RE-${event.contents.experiment}-${event.contents.variant}`,
                    },
                };
            default:
                throw new Error("Unknown event");
        }
    }
}
class Facebook {
    static itemValue(item) {
        return item.price * item.quantity;
    }
    static itemToContent(item) {
        return { id: item.name, quantity: item.quantity };
    }
    static addToCart(currency, item) {
        return {
            content_type: "product",
            contents: [this.itemToContent(item)],
            value: this.itemValue(item),
            currency: currency,
        };
    }
    static beginCheckout(currency, item) {
        return {
            contents: item.items.map((item) => this.itemToContent(item)),
            value: item.items
                .map((item) => this.itemValue(item))
                .reduce((acc, el) => acc + el, 0),
            num_items: item.items.length,
            currency: currency,
        };
    }
    static purchase(currency, item) {
        return {
            contents: item.items.map((item) => this.itemToContent(item)),
            value: item.total,
            currency: currency,
        };
    }
    static toPlatformEvent(currency, event) {
        switch (event.tag) {
            case "AddToCart":
                return {
                    name: "AddToCart",
                    value: this.addToCart(currency, event.contents),
                };
            case "LocateMe":
                return undefined;
            case "RemoveFromCart":
                return undefined;
            case "BeginCheckout":
                return {
                    name: "BeginCheckout",
                    value: this.beginCheckout(currency, event.contents),
                };
            case "Purchase":
                return {
                    name: "Purchase",
                    value: this.purchase(currency, event.contents),
                };
            case "SetCheckoutOption":
                return undefined;
            case "SuggestionResult":
            case "ExperienceImpression":
                return undefined;
            default:
                throw new Error("Unknown event");
        }
    }
}
function toNamedEvent(currency, event) {
    return {
        analytics_v3: AnalyticsV3.toPlatformEvent(currency, event),
        analytics_v4: AnalyticsV4.toPlatformEvent(currency, event),
        facebook_v2: Facebook.toPlatformEvent(currency, event),
        restaumatic: Restaumatic.toPlatformEvent(event),
    };
}
function getCurrencyEff() {
    return Skubacz.configuration.currency;
}
function trackEvent(event) {
    trackEventEff(toNamedEvent(getCurrencyEff(), event))();
}
function trackEventEff(event) {
    return function () {
        Skubacz.tracking({ tag: "TrackNamedEvent", event: event });
    };
}
// Send an event to all tracking platforms only once (dedup key stored in sessionStorage)
//
// See `Skubacz.trackNamedEvent` on the JS side.
export function trackEventUnique(eventKey, event) {
    trackEventUniqueEff(eventKey)(toNamedEvent(getCurrencyEff(), event))();
}
function trackEventUniqueEff(eventKey) {
    return function (event) {
        return function () {
            const key = "tracking_" + eventKey;
            // Local storage needs to be used here, because some mobile clients will kill and reload a tab multiple times,
            // causing duplicate events
            if (localStorage.getItem(key) !== "true") {
                Skubacz.tracking({ tag: "TrackNamedEvent", event: event });
                localStorage.setItem(key, "true");
            }
        };
    };
}
